// @ts-nocheck
/* Sound layer: one WebAudio context for the branded STING sonic (14 KB mono AAC,
   fetched lazily after the first gesture) and procedural game blips (0 bytes).
   WebView autoplay policy: the context must be created/resumed inside a user
   gesture - call unlock() from a pointerdown handler only. Degrades to silence
   if audio is unsupported or the asset is missing. */

const MUTE_KEY = "sting_muted";
const BRAND_URL = "/ds/assets/audio/sting-sonic.m4a";

let ctx = null;
let brand = null;
let brandPending = false;
let muted = (() => {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
})();

export const isMuted = () => muted;

export function setMuted(value) {
  muted = !!value;
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {}
}

// Must be called from a user gesture (pointerdown). Safe to call repeatedly:
// re-resumes the context after iOS suspends it (backgrounding, phone calls).
export function unlock() {
  if (ctx) {
    ctx.state !== "running" && ctx.resume && ctx.resume().catch(() => {});
    return;
  }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  try {
    ctx = new AC();
  } catch {
    return;
  }
  fetch(BRAND_URL)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
    .then((buf) => ctx.decodeAudioData(buf))
    .then((decoded) => {
      brand = decoded;
      if (brandPending) {
        brandPending = false;
        playBrand();
      }
    })
    .catch(() => {});
}

// Branded STING sonic: app start, mini-game finish, accepted receipt.
export function playBrand() {
  if (muted || !ctx) return;
  if (!brand) {
    brandPending = true; // first tap raced the decode - play as soon as it lands
    return;
  }
  try {
    const source = ctx.createBufferSource();
    source.buffer = brand;
    source.connect(ctx.destination);
    source.start();
  } catch {}
}

/* --- ZzFX generator (Zuper Zmall Zound Zynth) v1.3.2, MIT (c) Frank Force ---
   Vendored verbatim from KilledByAPixel/ZzFX (ZzFXMicro), minus its own
   AudioContext/playback tail - we render into our shared ctx so the mute flag,
   gesture unlock and iOS resume rules keep applying. Kept byte-faithful so
   sounds tuned in the playground (killedbyapixel.github.io/ZzFX) transfer 1:1. */
const zzfxR = 44100;
const zzfxV = 0.3;
// prettier-ignore
function zzfxG(p=1,k=.05,b=220,e=0,r=0,t=.1,q=0,D=1,u=0,y=0,v=0,z=0,l=0,E=0,A=0,F=0,c=0,w=1,m=0,B=0,N=0){
  let M=Math,d=2*M.PI,R=zzfxR,G=u*=500*d/R/R,C=b*=(1-k+2*k*M.random(k=[]))*d/R,
  g=0,H=0,a=0,n=1,I=0,J=0,f=0,h=N<0?-1:1,x=d*h*N*2/R,L=M.cos(x),Z=M.sin,K=Z(x)/4,O=1+K,
  X=-2*L/O,Y=(1-K)/O,P=(1+h*L)/2/O,Q=-(h+L)/O,S=P,T=0,U=0,V=0,W=0;
  e=R*e+9;m*=R;r*=R;t*=R;c*=R;y*=500*d/R**3;A*=d/R;v*=d/R;z*=R;l=R*l|0;p*=zzfxV;
  for(h=e+m+r+t+c|0;a<h;k[a++]=f*p)
    ++J%(100*F|0)||(f=q?1<q?2<q?3<q?4<q?(g/d%1<D/2)*2-1:Z(g**3):M.max(M.min(M.tan(g),1),-1):1-(2*g/d%2+2)%2:1-4*M.abs(M.round(g/d)-g/d):Z(g),
    f=(l?1-B+B*Z(d*a/l):1)*(4<q?f:(f<0?-1:1)*M.abs(f)**D)*(a<e?a/e:a<e+m?1-(a-e)/m*(1-w):a<e+m+r?w:a<h-c?(h-a-c)/t*w:0),
    f=c?f/2+(c>a?0:(a<h-c?1:(h-a)/c)*k[a-c|0]/2/p):f,
    N?f=W=S*T+Q*(T=U)+P*(U=f)-Y*V-X*(V=W):0),
    x=(b+=u+=y)*M.cos(A*H++),g+=x+x*E*Z(a**5),
    n&&++n>z&&(b+=v,C+=v,n=0),
    !l||++I%l||(b=C,u=G,n=n||1);
  return k;
}

/* Tool voices for the pit-stop fantasy: each spot kind sounds like its tool,
   not like a beep. Params are ZzFX playground arrays - retune by ear there and
   paste back. `streak` nudges playbackRate up per consecutive hit (the pitch-
   progression rule), capped so tier 8+ stays musical. */
// prettier-ignore
const TOOL_PARAMS = {
  nut:    [1.4,,132,,.05,.06,4,1.9,-1,,,,,.6,,.4,,.6,.02],    // impact-wrench ratchet burst
  wheel:  [1.6,,68,.001,.03,.16,1,2.6,-9,,,,,.2,,,,.8,.04],   // heavy hub thunk
  jack:   [1.1,.05,780,.001,.02,.1,,1,-42,,,,,1.1,,,,.7,.01], // pneumatic click-hiss
  miss:   [.9,,150,.002,.05,.15,2,.4,-3,,,,,.15],             // wrong tap: dull low buzz
  expire: [.6,.05,520,.001,.05,.22,,1,-38,,,,,1.4],           // spot lost: descending "pss"
  crackle:[.5,.3,900,,.01,.07,4,2.4,,,,,.01,1.8],             // electric product spark
  rev:    [1.2,.05,52,.02,.28,.34,2,1.1,16,,,,.05,.25],       // engine rev (car idle tap)
};

/* Hybrid slots: when a real recording lands at /ds/assets/audio/tools/<kind>.m4a,
   add the kind here - it replaces the synth voice with zero code changes.
   A failed fetch/decode falls back to the synth permanently for the session. */
const SAMPLE_KINDS = new Set([]);

const toolCache = {}; // kind -> AudioBuffer (decoded sample or rendered synth)

function toolBuffer(kind) {
  if (toolCache[kind]) return toolCache[kind];
  const params = TOOL_PARAMS[kind];
  if (!params) return null;
  const data = zzfxG(...params);
  const buf = ctx.createBuffer(1, data.length, zzfxR);
  buf.getChannelData(0).set(data);
  toolCache[kind] = buf;
  return buf;
}

function playBuffer(buf, rate) {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    src.connect(ctx.destination);
    src.start();
  } catch {}
}

// Pre-render synth voices off the hot path (call at game mount, during lights).
export function warmTools(kinds) {
  if (!ctx) return;
  try {
    kinds.forEach((k) => !SAMPLE_KINDS.has(k) && toolBuffer(k));
  } catch {}
}

export function playTool(kind, streak = 0) {
  if (muted || !ctx) return;
  const rate = 1 + Math.min(streak, 8) * 0.03;
  if (SAMPLE_KINDS.has(kind)) {
    const cached = toolCache[kind];
    if (cached) return playBuffer(cached, rate);
    fetch(`/ds/assets/audio/tools/${kind}.m4a`)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
      .then((raw) => ctx.decodeAudioData(raw))
      .then((decoded) => {
        toolCache[kind] = decoded;
        playBuffer(decoded, rate);
      })
      .catch(() => {
        SAMPLE_KINDS.delete(kind); // broken asset: synth takes over for good
        playTool(kind, streak);
      });
    return;
  }
  const buf = toolBuffer(kind);
  if (buf) playBuffer(buf, rate);
}

// Procedural blip for game events (start lights, hit, penalty).
export function beep(freq = 880, dur = 0.06, vol = 0.12) {
  if (muted || !ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  } catch {}
}
